import { useEffect, useRef } from "react";

/**
 * Wraps a wide, dense table with:
 *  - a slim top scrollbar bidirectionally synced to the table's own (bottom)
 *    scroll region, so staff can pan horizontally without scrolling down
 *    through dozens of rows to find the real scrollbar first;
 *  - the vertical scroll clamp (`maxHeight`) that keeps that bottom
 *    scrollbar on-screen instead of sinking below the row list.
 *
 * The table itself still owns its sticky header / sticky columns (via
 * `[&_th]:sticky` etc. on the <table> element) — this component only owns
 * the two scroll regions and keeping them in sync.
 */
export default function ErgonomicTableContainer({
  children,
  maxHeight = "calc(100vh - 280px)",
  minWidth = "1300px",
  className = "",
  testId,
}) {
  const topRef = useRef(null);
  const bottomRef = useRef(null);
  const isSyncing = useRef(false);

  useEffect(() => {
    const top = topRef.current;
    const bottom = bottomRef.current;
    if (!top || !bottom) return;

    // Mutual guard: whichever side we just set programmatically consumes
    // the scroll event it triggers instead of echoing it back, which is
    // what would otherwise create an infinite scroll-sync loop.
    const onTopScroll = () => {
      if (isSyncing.current) { isSyncing.current = false; return; }
      isSyncing.current = true;
      bottom.scrollLeft = top.scrollLeft;
    };
    const onBottomScroll = () => {
      if (isSyncing.current) { isSyncing.current = false; return; }
      isSyncing.current = true;
      top.scrollLeft = bottom.scrollLeft;
    };

    top.addEventListener("scroll", onTopScroll, { passive: true });
    bottom.addEventListener("scroll", onBottomScroll, { passive: true });
    return () => {
      top.removeEventListener("scroll", onTopScroll);
      bottom.removeEventListener("scroll", onBottomScroll);
    };
  }, []);

  return (
    <div className={className} data-testid={testId}>
      <div
        ref={topRef}
        className="w-full overflow-x-auto overflow-y-hidden scrollbar-thin"
        style={{ height: 14 }}
        aria-hidden="true"
      >
        <div style={{ minWidth, height: 1 }} />
      </div>
      <div
        ref={bottomRef}
        className="w-full overflow-x-auto overflow-y-auto scrollbar-thin"
        style={{ maxHeight }}
        data-testid={testId ? `${testId}-scroll` : undefined}
      >
        {children}
      </div>
    </div>
  );
}
